export interface UploadFileNameParts {
  baseName: string;
  extension: string;
}

export const mimeToExtension = (mimeType: string) => {
  const mime = (mimeType || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  const mapping: Record<string, string> = {
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "image/svg+xml": ".svg",
    "application/pdf": ".pdf",
    "text/plain": ".txt",
    "text/csv": ".csv",
    "application/json": ".json",
    "application/zip": ".zip",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
    "application/msword": ".doc",
  };
  if (mapping[mime]) return mapping[mime];

  if (mime.includes("pdf")) return ".pdf";

  const [, subtypeRaw = ""] = mime.split("/", 2);
  const subtype = subtypeRaw.split("+")[0];
  if (/^[a-z0-9][a-z0-9.-]{0,15}$/.test(subtype)) {
    const subtypeMapping: Record<string, string> = {
      jpeg: ".jpg",
      jpg: ".jpg",
      png: ".png",
      gif: ".gif",
      webp: ".webp",
      csv: ".csv",
      json: ".json",
      plain: ".txt",
      zip: ".zip",
      pdf: ".pdf",
    };
    return subtypeMapping[subtype] || "";
  }
  return "";
};

export const splitFileNameForUpload = (
  fileName: string,
  mimeType: string,
): UploadFileNameParts => {
  const rawName = (fileName || "").trim();
  const lastDotIndex = rawName.lastIndexOf(".");
  const extensionCandidate =
    lastDotIndex > 0 && lastDotIndex < rawName.length - 1
      ? rawName.slice(lastDotIndex + 1)
      : "";
  const hasExplicitExtension =
    !!extensionCandidate && /^[a-zA-Z0-9]{1,16}$/.test(extensionCandidate);
  if (hasExplicitExtension) {
    return {
      baseName: rawName.slice(0, lastDotIndex),
      extension: rawName.slice(lastDotIndex),
    };
  }
  return {
    baseName: rawName,
    extension: mimeToExtension(mimeType),
  };
};

export const detectExtensionFromFileSignature = async (file: File) => {
  try {
    const head = new Uint8Array(await file.slice(0, 8192).arrayBuffer());
    const tailStart = Math.max(0, file.size - 65536);
    const tail = new Uint8Array(await file.slice(tailStart).arrayBuffer());
    const toAscii = (bytes: Uint8Array) =>
      String.fromCharCode(...Array.from(bytes));

    if (head.length >= 4) {
      if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return ".jpeg";
      if (
        head[0] === 0x89 &&
        head[1] === 0x50 &&
        head[2] === 0x4e &&
        head[3] === 0x47
      )
        return ".png";
      if (
        head[0] === 0x47 &&
        head[1] === 0x49 &&
        head[2] === 0x46 &&
        head[3] === 0x38
      )
        return ".gif";
      if (
        head[0] === 0x25 &&
        head[1] === 0x50 &&
        head[2] === 0x44 &&
        head[3] === 0x46
      )
        return ".pdf";
      if (head[0] === 0x50 && head[1] === 0x4b) {
        const zipCatalog = `${toAscii(head)}${toAscii(tail)}`;
        if (zipCatalog.includes("xl/")) return ".xlsx";
        if (zipCatalog.includes("word/")) return ".docx";
        if (zipCatalog.includes("ppt/")) return ".pptx";
        if (zipCatalog.includes("odt")) return ".odt";
        if (zipCatalog.includes("ods")) return ".ods";
        if (zipCatalog.includes("odp")) return ".odp";
        return ".zip";
      }
      if (head[0] === 0x42 && head[1] === 0x4d) return ".bmp";
      if (
        (head[0] === 0x49 && head[1] === 0x49 && head[2] === 0x2a && head[3] === 0x00) ||
        (head[0] === 0x4d && head[1] === 0x4d && head[2] === 0x00 && head[3] === 0x2a)
      )
        return ".tif";
      if (
        head.length >= 12 &&
        head[0] === 0x52 &&
        head[1] === 0x49 &&
        head[2] === 0x46 &&
        head[3] === 0x46 &&
        head[8] === 0x57 &&
        head[9] === 0x45 &&
        head[10] === 0x42 &&
        head[11] === 0x50
      )
        return ".webp";
      if (
        (head[0] === 0x49 && head[1] === 0x44 && head[2] === 0x33) ||
        (head[0] === 0xff && (head[1] === 0xfb || head[1] === 0xf3 || head[1] === 0xf2))
      )
        return ".mp3";
      if (
        head.length >= 12 &&
        head[4] === 0x66 &&
        head[5] === 0x74 &&
        head[6] === 0x79 &&
        head[7] === 0x70
      )
        return ".mp4";
      if (head[0] === 0x1f && head[1] === 0x8b) return ".gz";
      if (
        head.length >= 7 &&
        head[0] === 0x52 &&
        head[1] === 0x61 &&
        head[2] === 0x72 &&
        head[3] === 0x21 &&
        head[4] === 0x1a &&
        head[5] === 0x07
      )
        return ".rar";
      if (
        head.length >= 6 &&
        head[0] === 0x37 &&
        head[1] === 0x7a &&
        head[2] === 0xbc &&
        head[3] === 0xaf &&
        head[4] === 0x27 &&
        head[5] === 0x1c
      )
        return ".7z";
      if (
        head.length >= 8 &&
        head[0] === 0xd0 &&
        head[1] === 0xcf &&
        head[2] === 0x11 &&
        head[3] === 0xe0 &&
        head[4] === 0xa1 &&
        head[5] === 0xb1 &&
        head[6] === 0x1a &&
        head[7] === 0xe1
      )
        return ".xls";
      if (
        head.length > 262 &&
        head[257] === 0x75 &&
        head[258] === 0x73 &&
        head[259] === 0x74 &&
        head[260] === 0x61 &&
        head[261] === 0x72
      )
        return ".tar";
    }

    if (head.length) {
      let nonTextCount = 0;
      for (let i = 0; i < head.length; i += 1) {
        const b = head[i];
        const isControl = b < 9 || (b > 13 && b < 32);
        if (isControl) nonTextCount += 1;
      }
      if (nonTextCount / head.length < 0.02) {
        const text = new TextDecoder("utf-8", { fatal: false }).decode(head);
        const trimmed = text.trimStart();
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) return ".json";
        if (trimmed.startsWith("<?xml") || trimmed.startsWith("<")) return ".xml";

        const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
        const sample = lines.slice(0, 10);
        const commaRows = sample.filter((line) => line.includes(",")).length;
        const semicolonRows = sample.filter((line) => line.includes(";")).length;
        if (sample.length >= 2 && (commaRows >= 2 || semicolonRows >= 2)) return ".csv";
        return ".txt";
      }
    }
  } catch {
    return "";
  }
  return "";
};
